// Personal crews (04 §crews/me, role-skill-policies; 02 §personal_crew … §sharing_policy; 05
// §Personal crews; 11 §crew caps). M4-01. Everything is the `agents` add-on (ADR-0016 §8: crews
// are part of `agents`). `crews/me/*` is the signed-in person's own crew only: no route lists or
// reads another person's crew, admins included (02 §Personal-crew invariants). Admins set the
// ceiling: role skill policies and the company's crew caps.
import { z } from "zod";
import {
  CREW_DRAFT,
  CREW_TEXT,
  CrewAgent,
  CrewCap,
  CrewContextLayerRow,
  CrewMemoryGrant,
  CrewProposalRow,
  PersonalCrew,
  RoleSkillPolicy,
} from "../crew-rows";
import {
  AutonomyMode,
  CrewActionKind,
  CrewCapPeriod,
  CrewContextLayer,
  CrewContextVisibility,
  CrewMemoryBucket,
  CrewMemorySubjectKind,
  CrewProposalState,
  Currency,
  InboxItemKind,
  InboxSubjectKind,
  MembershipStatus,
  RunIdentityMode,
  SharingRule,
  SharingSubjectKind,
} from "../enums";
import { RecallMemoryCopy, Reminder } from "../memory-rows";
import {
  BoundedJson,
  boundedRecord,
  IsoDate,
  LIMITS,
  MinorAmount,
  Timestamp,
  text,
  Uuid,
} from "../primitives";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const agents = { feature: "agents" } as const;

// Views -----------------------------------------------------------------------------------------

/** One action kind's effective autonomy: the owner's row, or the default (`ask`, `as_agent`). */
export const CrewAutonomyEntry = z
  .object({
    action_kind: CrewActionKind,
    mode: AutonomyMode,
    identity_mode: RunIdentityMode,
    constraints: BoundedJson,
    /** `alone` is allowed only where a role skill policy of the owner allows the action kind. */
    alone_allowed: z.boolean(),
    source: z.enum(["policy", "default"]),
  })
  .meta({ id: "CrewAutonomyEntry" });

export const CrewSharingEntry = z
  .object({
    subject_kind: SharingSubjectKind,
    rule: SharingRule,
    source: z.enum(["policy", "default"]),
  })
  .meta({ id: "CrewSharingEntry" });

/** One interview question (role-parameterised: it names the owner's seat and company). */
export const CrewInterviewQuestion = z
  .object({
    id: z.string(),
    prompt: z.string(),
    hint: z.string(),
    /** The context layer the answer is written into. */
    layer: CrewContextLayer,
    /** Answers that must be pasted verbatim (writing samples): never typed summaries. */
    verbatim: z.boolean(),
  })
  .meta({ id: "CrewInterviewQuestion" });

export const CrewCaps = z
  .object({
    period_type: CrewCapPeriod,
    currency: Currency,
    /** The owner's own cap (null: the ceiling applies). */
    cap_minor: MinorAmount.nullable(),
    /** The lowest of the company's per-crew cap and the owner's role policies' budgets. */
    ceiling_minor: MinorAmount.nullable(),
    /** What applies: the lower of the two. */
    effective_minor: MinorAmount.nullable(),
    spent_minor: MinorAmount,
  })
  .meta({ id: "CrewCaps" });

/** M4-03: the crew features' settings (the daily brief, inbox triage, the heartbeat). */
export const CrewFeatures = z
  .object({
    triage_enabled: z.boolean(),
    brief: z.object({
      enabled: z.boolean(),
      /** "HH:MM" in the company's timezone. */
      time: z.string(),
      /** ISO weekdays, 1 = Monday. */
      days: z.array(z.int().min(1).max(7)),
      /** The local date of the last brief posted. */
      last_on: IsoDate.nullable(),
    }),
    /** RRULEs (company timezone): each occurrence wakes the crew (`heartbeat`). */
    heartbeat_schedule: z.array(z.string()),
  })
  .meta({ id: "CrewFeatures" });

/** `GET crews/me`: my crew, its agents, policies, context and interview (04). */
export const CrewView = z
  .object({
    crew: PersonalCrew,
    agent: z.object({
      membership_id: Uuid,
      display_name: z.string(),
      status: MembershipStatus,
    }),
    crew_agents: z.array(CrewAgent),
    autonomy: z.array(CrewAutonomyEntry),
    sharing: z.array(CrewSharingEntry),
    context_layers: z.array(CrewContextLayerRow),
    interview: z.object({
      questions: z.array(CrewInterviewQuestion),
      answers: z.record(z.string(), z.string()),
      complete: z.boolean(),
    }),
    caps: CrewCaps,
    features: CrewFeatures,
  })
  .meta({ id: "CrewView" });
export type CrewView = z.infer<typeof CrewView>;

/** `POST crews/me/brief`: the day's brief in `#brief` (an existing one for the day is returned). */
export const CrewBrief = z
  .object({
    message_id: Uuid,
    thread_id: Uuid,
    channel_id: Uuid,
    on: IsoDate,
    text: z.string(),
  })
  .meta({ id: "CrewBrief" });

/** A proposal as the owner sees it, with a summary of its inbox item when it has one. */
export const CrewProposal = CrewProposalRow.extend({
  item: z
    .object({
      kind: InboxItemKind,
      summary: z.string(),
      subject_kind: InboxSubjectKind,
      subject_id: Uuid,
    })
    .nullable(),
}).meta({ id: "CrewProposal" });

/** One role-memory subject the owner may grant (OQ 37). */
export const CrewMemorySubject = z
  .object({
    bucket: CrewMemoryBucket,
    subject_kind: CrewMemorySubjectKind,
    subject_id: Uuid,
    label: z.string(),
  })
  .meta({ id: "CrewMemorySubject" });

export const CrewMemoryGrants = z
  .object({ data: z.array(CrewMemoryGrant), available: z.array(CrewMemorySubject) })
  .meta({ id: "CrewMemoryGrants" });

// Bodies ----------------------------------------------------------------------------------------

export const CrewPatch = z
  .strictObject({
    /** The crew's display name (default "{owner}'s agent"). */
    display_name: text(LIMITS.name).optional(),
    heartbeat_schedule: z.array(text(LIMITS.text)).max(8).optional(),
    max_concurrency: z.int().min(1).max(8).optional(),
    model_policy: BoundedJson.optional(),
    /** Lower the crew's cap (never above the ceiling); null returns to the ceiling. */
    cap_minor: MinorAmount.min(0).nullable().optional(),
  })
  .meta({ id: "CrewPatch" });

export const AutonomyInput = z
  .strictObject({
    action_kind: CrewActionKind,
    mode: AutonomyMode,
    identity_mode: RunIdentityMode.default("as_agent"),
    constraints: BoundedJson.default({}),
  })
  .meta({ id: "AutonomyInput" });
/** The whole set: kinds left out return to the default (`ask`, `as_agent`). */
export const AutonomyPut = z
  .strictObject({ policies: z.array(AutonomyInput).max(CrewActionKind.options.length) })
  .meta({ id: "AutonomyPut" });

export const SharingInput = z
  .strictObject({ subject_kind: SharingSubjectKind, rule: SharingRule })
  .meta({ id: "SharingInput" });
/** The whole set: kinds left out return to the default (`if_requester_can_see`). */
export const SharingPut = z
  .strictObject({ rules: z.array(SharingInput).max(SharingSubjectKind.options.length) })
  .meta({ id: "SharingPut" });

export const CrewAgentCreate = z
  .strictObject({
    name: text(120),
    skill_pack_ids: z.array(Uuid).max(LIMITS.list).default([]),
  })
  .meta({ id: "CrewAgentCreate" });
export const CrewAgentParams = z.object({ co: Uuid, id: Uuid });

/** Interview answers, by question id; resumable (each call adds to what was answered). */
export const OnboardingAnswers = z
  .strictObject({
    answers: boundedRecord(z.string().max(LIMITS.longText), 20),
    /** Write the answers into the context layers (the interview's closing step). */
    complete: z.boolean().default(false),
  })
  .meta({ id: "OnboardingAnswers" });

export const ContextLayerParams = z.object({ co: Uuid, layer: CrewContextLayer });
export const ContextLayerPut = z
  .strictObject({
    body: z.string().max(20_000),
    visibility: CrewContextVisibility.default("owner_only"),
  })
  .meta({ id: "ContextLayerPut" });

export const RoleSkillPolicyParams = z.object({ co: Uuid, role_id: Uuid });
export const RoleSkillPolicyPut = z
  .strictObject({
    allowed_skill_pack_ids: z.array(Uuid).max(LIMITS.list).default([]),
    default_skill_pack_ids: z.array(Uuid).max(LIMITS.list).default([]),
    budget_money_cents: MinorAmount.min(0).nullable().default(null),
    budget_period: CrewCapPeriod.default("month"),
    allowed_action_kinds: z.array(CrewActionKind).max(CrewActionKind.options.length).default([]),
  })
  .refine((b) => b.default_skill_pack_ids.every((id) => b.allowed_skill_pack_ids.includes(id)), {
    message: "default_skill_pack_ids must be allowed",
    path: ["default_skill_pack_ids"],
  })
  .meta({ id: "RoleSkillPolicyPut" });

export const CrewCapPut = z
  .strictObject({
    per_crew_cap_minor: MinorAmount.min(0).nullable(),
    company_cap_minor: MinorAmount.min(0).nullable(),
    period_type: CrewCapPeriod.default("month"),
  })
  .meta({ id: "CrewCapPut" });

// M4-03 bodies -----------------------------------------------------------------------------------

/** The whole feature set (each RRULE must parse: 422 otherwise). */
export const CrewFeaturesPut = z
  .strictObject({
    triage_enabled: z.boolean(),
    brief: z.strictObject({
      enabled: z.boolean(),
      time: z
        .string()
        .max(5)
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM"),
      days: z.array(z.int().min(1).max(7)).max(7),
    }),
    heartbeat_schedule: z.array(text(LIMITS.text)).max(8),
  })
  .meta({ id: "CrewFeaturesPut" });

export const CrewProposalParams = z.object({ co: Uuid, id: Uuid });
export const CrewProposalsQuery = z
  .object({ state: CrewProposalState.default("pending") })
  .meta({ id: "CrewProposalsQuery" });

const reason = z.string().max(CREW_TEXT).default("");
/** What a crew run proposes (`crews/run/proposals`), by kind. */
export const CrewProposalCreate = z
  .discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("done"), inbox_item_id: Uuid, reason }),
    z.strictObject({
      kind: z.literal("snooze"),
      inbox_item_id: Uuid,
      snooze_until: Timestamp,
      reason,
    }),
    z.strictObject({
      kind: z.literal("reply"),
      inbox_item_id: Uuid,
      body_markdown: z.string().min(1).max(CREW_DRAFT),
      reason,
    }),
    z.strictObject({
      kind: z.literal("remind"),
      text: z.string().min(1).max(CREW_TEXT),
      remind_at: Timestamp,
      reason,
    }),
    z.strictObject({
      kind: z.literal("task_update"),
      task_id: Uuid,
      status: z.string().min(1).max(LIMITS.key).optional(),
      comment: z.string().min(1).max(CREW_DRAFT).optional(),
      reason,
    }),
  ])
  .refine((b) => b.kind !== "task_update" || b.status !== undefined || b.comment !== undefined, {
    message: "a task update needs a status or a comment",
    path: ["status"],
  })
  .meta({ id: "CrewProposalCreate" });

export const CrewReminderCreate = z
  .strictObject({
    text: z.string().min(1).max(CREW_TEXT),
    at: Timestamp,
    /** A seat I hold (default: my first active seat). */
    seat_id: Uuid.optional(),
  })
  .meta({ id: "CrewReminderCreate" });
export const ReminderParams = z.object({ co: Uuid, id: Uuid });
/** ACTIONS: an open reminder (not fired, not dismissed) re-worded or moved (to the future). */
export const CrewReminderPatch = z
  .strictObject({ text: z.string().min(1).max(CREW_TEXT).optional(), at: Timestamp.optional() })
  .refine((p) => p.text !== undefined || p.at !== undefined, { message: "text or at" })
  .meta({ id: "CrewReminderPatch" });

export const CrewMemoryGrantsPut = z
  .strictObject({
    grants: z.array(z.strictObject({ bucket: CrewMemoryBucket, subject_id: Uuid })).max(32),
  })
  .meta({ id: "CrewMemoryGrantsPut" });

/**
 * D12 (personal-space read grant): whether my agent may READ my personal memory in Recall. Never
 * write: Recall offers read (`recall`) or nothing. Set by me only, never by an admin or my agent.
 */
export const CrewPersonalMemoryPut = z
  .strictObject({ access: z.enum(["read", "none"]) })
  .meta({ id: "CrewPersonalMemoryPut" });

export const CrewPersonalMemory = z
  .object({
    access: z.enum(["read", "none"]),
    /** What Recall answers my agent now holds on my personal space (`recall`, `none`, …). */
    effective: z.string().max(32),
  })
  .meta({ id: "CrewPersonalMemory" });

/**
 * D12 consent screen: my agents, what I let each read of my personal memory (read only), whether I
 * can grant it now and why not, and the log of my grants and revokes, newest first (at most 50).
 */
export const CrewPersonalMemoryState = z
  .object({
    agents: z.array(
      z.object({
        membership_id: Uuid,
        name: z.string(),
        /** What I last set (`none` when never granted). */
        access: z.enum(["read", "none"]),
        /** Granting is available now; taking a grant back always is. */
        can_grant: z.boolean(),
        /** Why granting is not available, in words; null when it is. */
        unavailable: z.string().nullable(),
      }),
    ),
    log: z.array(
      z.object({
        at: Timestamp,
        agent_membership_id: Uuid,
        agent_name: z.string(),
        access: z.enum(["read", "none"]),
        effective: z.string().max(32),
      }),
    ),
  })
  .meta({ id: "CrewPersonalMemoryState" });

/**
 * `POST crews/me/hire-as-de` (04 §crews/me; M7-05): a new seat (supervisor required) filled by a
 * new DE hired from my crew, and the memory groups my crew was granted (OQ 37) copied into the
 * DE's `seat:{id}` bucket with new provenance. Only groups the crew holds now may be named.
 */
export const HireAsDe = z
  .strictObject({
    display_name: text(LIMITS.name),
    /** The DE template (`recruiting_de` seeds its metric set; any other key seeds none). */
    template_key: text(LIMITS.name).optional(),
    seat: z.strictObject({
      role_id: Uuid,
      title: text(LIMITS.name),
      supervisor_seat_id: Uuid,
    }),
    memory_groups: z.array(z.strictObject({ bucket: CrewMemoryBucket, subject_id: Uuid })).max(32),
  })
  .meta({ id: "HireAsDe" });

export const HireAsDeResult = z
  .object({
    seat_id: Uuid,
    de_membership_id: Uuid,
    /** The pending copy (null when no memory group was chosen). */
    copy: RecallMemoryCopy.nullable(),
  })
  .meta({ id: "HireAsDeResult" });

// Routes ----------------------------------------------------------------------------------------

const me = (id: string, summary: string) => ({
  ...agents,
  id,
  tag: "crews",
  summary,
  access: "member" as const,
  params: CompanyParams,
});

export const crewRoutes = {
  "crews.me": defineRoute({
    ...me("crews.me", "My crew: its agent, crew agents, policies, context and interview"),
    method: "get",
    path: `${base}/crews/me`,
    response: { status: 200, schema: CrewView },
    errors: [],
    emits: [],
  }),
  "crews.update": defineRoute({
    ...me("crews.update", "Rename my agent, set its schedule, concurrency, model policy or cap"),
    method: "patch",
    path: `${base}/crews/me`,
    body: CrewPatch,
    idempotency: "optional",
    response: { status: 200, schema: CrewView },
    errors: [],
    emits: ["personal_crew.updated", "membership.updated"],
  }),
  "crews.putAutonomy": defineRoute({
    ...me("crews.putAutonomy", "Set my agent's autonomy per action kind (within the ceiling)"),
    method: "put",
    path: `${base}/crews/me/autonomy`,
    body: AutonomyPut,
    idempotency: "optional",
    response: { status: 200, schema: CrewView },
    errors: [],
    emits: ["autonomy_policy.changed"],
  }),
  "crews.putSharing": defineRoute({
    ...me("crews.putSharing", "Set what my agent discloses to other people's agents"),
    method: "put",
    path: `${base}/crews/me/sharing`,
    body: SharingPut,
    idempotency: "optional",
    response: { status: 200, schema: CrewView },
    errors: [],
    emits: ["sharing_policy.changed"],
  }),
  "crews.addAgent": defineRoute({
    ...me("crews.addAgent", "Add a crew agent (skill packs within my role's policy)"),
    method: "post",
    path: `${base}/crews/me/crew-agents`,
    body: CrewAgentCreate,
    idempotency: "optional",
    response: { status: 201, schema: CrewAgent },
    errors: ["conflict"],
    emits: ["crew_agent.added"],
  }),
  "crews.removeAgent": defineRoute({
    ...me("crews.removeAgent", "Remove a crew agent I added (the role default stays)"),
    method: "delete",
    path: `${base}/crews/me/crew-agents/{id}`,
    params: CrewAgentParams,
    response: { status: 200, schema: CrewAgent },
    errors: ["conflict"],
    emits: ["crew_agent.removed"],
  }),
  "crews.answerOnboarding": defineRoute({
    ...me("crews.answerOnboarding", "Answer the interview (resumable; `complete` writes context)"),
    method: "post",
    path: `${base}/crews/me/onboarding/answers`,
    body: OnboardingAnswers,
    idempotency: "optional",
    response: { status: 200, schema: CrewView },
    errors: [],
    emits: ["personal_crew.onboarding_answered", "crew_context.updated"],
  }),
  "crews.putContextLayer": defineRoute({
    ...me("crews.putContextLayer", "Write one context layer (supersedes the current one)"),
    method: "put",
    path: `${base}/crews/me/context/{layer}`,
    params: ContextLayerParams,
    body: ContextLayerPut,
    idempotency: "optional",
    response: { status: 200, schema: CrewContextLayerRow },
    errors: [],
    emits: ["crew_context.updated"],
  }),
  "crews.pause": defineRoute({
    ...me("crews.pause", "Pause my agent (no wake-up starts a run)"),
    method: "post",
    path: `${base}/crews/me/pause`,
    idempotency: "optional",
    response: { status: 200, schema: CrewView },
    errors: [],
    emits: ["personal_crew.paused"],
  }),
  "crews.resume": defineRoute({
    ...me("crews.resume", "Resume my agent"),
    method: "post",
    path: `${base}/crews/me/resume`,
    idempotency: "optional",
    response: { status: 200, schema: CrewView },
    errors: ["budget_exhausted"],
    emits: ["personal_crew.resumed"],
  }),

  // M4-03: the crew features (the owner's settings, brief, proposals, reminders, memory grants)
  "crews.putFeatures": defineRoute({
    ...me("crews.putFeatures", "Set my agent's daily brief, inbox triage and heartbeat schedule"),
    method: "put",
    path: `${base}/crews/me/features`,
    body: CrewFeaturesPut,
    idempotency: "optional",
    response: { status: 200, schema: CrewView },
    errors: [],
    emits: ["personal_crew.features_changed"],
  }),
  "crews.brief": defineRoute({
    ...me("crews.brief", "Brief me now: today's brief in #brief (once a day; repeats return it)"),
    method: "post",
    path: `${base}/crews/me/brief`,
    idempotency: "optional",
    response: { status: 201, schema: CrewBrief },
    errors: ["crew_paused"],
    emits: ["topic.created", "message.created", "crew_brief.posted"],
  }),
  "crews.proposals": defineRoute({
    ...me("crews.proposals", "My agent's proposals (default: pending), newest first"),
    method: "get",
    path: `${base}/crews/me/proposals`,
    query: CrewProposalsQuery,
    response: { status: 200, schema: z.object({ data: z.array(CrewProposal) }) },
    errors: [],
    emits: [],
  }),
  "crews.acceptProposal": defineRoute({
    ...me("crews.acceptProposal", "Accept a proposal: it is applied (a reply is posted)"),
    method: "post",
    path: `${base}/crews/me/proposals/{id}/accept`,
    params: CrewProposalParams,
    idempotency: "optional",
    response: { status: 200, schema: CrewProposal },
    errors: ["conflict", "no_seat"],
    emits: [
      "crew_proposal.decided",
      "inbox_item.done",
      "inbox_item.snoozed",
      "approval.decided",
      "reminder.created",
      "task.status_changed",
      "message.created",
    ],
  }),
  "crews.dismissProposal": defineRoute({
    ...me("crews.dismissProposal", "Dismiss a proposal (a reply's approval is rejected)"),
    method: "post",
    path: `${base}/crews/me/proposals/{id}/dismiss`,
    params: CrewProposalParams,
    idempotency: "optional",
    response: { status: 200, schema: CrewProposal },
    errors: ["conflict"],
    emits: ["crew_proposal.decided", "approval.decided"],
  }),
  "crews.reminders": defineRoute({
    ...me("crews.reminders", "My open reminders (soonest first) and those fired in the last day"),
    method: "get",
    path: `${base}/crews/me/reminders`,
    response: { status: 200, schema: z.object({ data: z.array(Reminder) }) },
    errors: [],
    emits: [],
  }),
  "crews.addReminder": defineRoute({
    ...me("crews.addReminder", "Set a reminder on a seat I hold"),
    method: "post",
    path: `${base}/crews/me/reminders`,
    body: CrewReminderCreate,
    idempotency: "optional",
    response: { status: 201, schema: Reminder },
    errors: ["no_seat"],
    emits: ["reminder.created"],
  }),
  "crews.updateReminder": defineRoute({
    ...me("crews.updateReminder", "Change the text or time of an open reminder of a seat I hold"),
    method: "patch",
    path: `${base}/crews/me/reminders/{id}`,
    params: ReminderParams,
    body: CrewReminderPatch,
    response: { status: 200, schema: Reminder },
    errors: ["conflict"],
    emits: ["reminder.updated"],
  }),
  "crews.dismissReminder": defineRoute({
    ...me("crews.dismissReminder", "Dismiss a reminder of a seat I hold"),
    method: "post",
    path: `${base}/crews/me/reminders/{id}/dismiss`,
    params: ReminderParams,
    idempotency: "optional",
    response: { status: 200, schema: Reminder },
    errors: [],
    emits: ["reminder.dismissed"],
  }),
  "crews.memoryGrants": defineRoute({
    ...me("crews.memoryGrants", "The role memory my agent may read, and what I may grant (OQ 37)"),
    method: "get",
    path: `${base}/crews/me/memory-grants`,
    response: { status: 200, schema: CrewMemoryGrants },
    errors: [],
    emits: [],
  }),
  "crews.putMemoryGrants": defineRoute({
    ...me(
      "crews.putMemoryGrants",
      "Grant role memory to my agent (the whole set; nothing by default)",
    ),
    method: "put",
    path: `${base}/crews/me/memory-grants`,
    body: CrewMemoryGrantsPut,
    idempotency: "optional",
    response: { status: 200, schema: CrewMemoryGrants },
    errors: ["grant_not_allowed"],
    emits: ["crew_memory_grant.changed"],
  }),
  "crews.personalMemory": defineRoute({
    ...me(
      "crews.personalMemory",
      "My agents' read access to my personal memory, and my log of grants and revokes (D12)",
    ),
    // As the PUT: readable with the add-on off, so a grant can always be seen and taken back.
    feature: "core",
    description:
      "My own session only (a key, a run token or an admin gets 403); an owner or admin sees only " +
      "their own agents here, never anyone else's. `can_grant` false names why in `unavailable`: " +
      "memory off, my agent has no Recall identity yet, my agent is not yet linked to me in Recall " +
      "(pending approval), memory not set up for me, or Recall not offering the consent step " +
      "(`GET /install` lists no `consent` act). The log is my `recall.personal_grant_changed` events.",
    method: "get",
    path: `${base}/crews/me/personal-memory`,
    response: { status: 200, schema: CrewPersonalMemoryState },
    errors: [],
    emits: [],
  }),
  "crews.putPersonalMemory": defineRoute({
    ...me(
      "crews.putPersonalMemory",
      "Let my agent read my personal memory in Recall, or take it back (D12)",
    ),
    // Not gated on the `agents` add-on as a whole: taking it back (`none`) must work in grace
    // or with the add-on off; granting (`read`) is gated in the handler (`agents` writable,
    // `memory` active).
    feature: "core",
    description:
      "My own session only (a key, a run token or an admin gets 403). Granting needs the `agents` " +
      "add-on writable (else 403 `feature_read_only`/`feature_disabled`) and memory on; taking it " +
      "back never waits on a subscription. Recall is asked as me (the " +
      "install key plus a subject assertion of act `consent`) to let my agent read my personal " +
      "space (`read`) or not (`none`); Recall logs it and holds it from the next request. 409 when " +
      "memory is not set up for me, my agent has no Recall identity, or Recall does not know my " +
      "agent as mine (not bound to me); 503 while Recall cannot be reached. NOT YET USABLE: " +
      "CompanyOS does not bind crew agents to their person in Recall yet (pending approval), so " +
      "until it does every call answers 409 saying so.",
    method: "put",
    path: `${base}/crews/me/personal-memory`,
    body: CrewPersonalMemoryPut,
    idempotency: "optional",
    response: { status: 200, schema: CrewPersonalMemory },
    errors: ["conflict", "not_ready"],
    emits: ["recall.personal_grant_changed"],
  }),
  // M7-05
  "crews.hireAsDe": defineRoute({
    ...me(
      "crews.hireAsDe",
      "Hire my agent as a DE: a new supervised seat and DE, my agent's chosen memory copied in",
    ),
    description:
      "Needs `company#manager` (creating a seat), like `POST seats` and `POST digital-employees`. " +
      "`memory_groups` must be groups my crew holds now (`crews/me/memory-grants`) and that I may " +
      "still grant; personal memory is never a group. The copy runs in the background " +
      "(`recall.provision`): its state is on the DE's `recall-agent`.",
    method: "post",
    path: `${base}/crews/me/hire-as-de`,
    body: HireAsDe,
    idempotency: "optional",
    response: { status: 201, schema: HireAsDeResult },
    errors: ["grant_not_allowed", "supervisor_required", "validation_failed", "agent_mode_managed"],
    emits: [
      "seat.created",
      "membership.created",
      "digital_employee.created",
      "seat.assigned",
      "access.granted",
      "seat_metric.changed",
      "target_rule.changed",
      "crew.hired_as_de",
      // AGENT-RECORD: catalog entries an older company lacks (domain/org/catalog.ts)
      "metric_def.created",
    ],
  }),
  "crewRun.propose": defineRoute({
    ...agents,
    id: "crewRun.propose",
    method: "post",
    path: `${base}/crews/run/proposals`,
    tag: "crews",
    summary: "A crew run proposes to its owner (applied now where the owner allows `alone`)",
    access: "run_token",
    params: CompanyParams,
    body: CrewProposalCreate,
    idempotency: "optional",
    response: { status: 201, schema: CrewProposal },
    errors: ["crew_paused", "triage_off", "autonomy_never", "no_seat"],
    emits: [
      "crew_proposal.created",
      "approval.requested",
      "inbox_item.done",
      "inbox_item.snoozed",
      "message.created",
      "reminder.created",
      "task.status_changed",
    ],
  }),

  "roleSkillPolicies.list": defineRoute({
    ...agents,
    id: "roleSkillPolicies.list",
    method: "get",
    path: `${base}/role-skill-policies`,
    tag: "crews",
    summary: "Role skill policies: the ceiling of each role's crews (admins)",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: z.object({ data: z.array(RoleSkillPolicy) }) },
    errors: [],
    emits: [],
  }),
  "roleSkillPolicies.put": defineRoute({
    ...agents,
    id: "roleSkillPolicies.put",
    method: "put",
    path: `${base}/role-skill-policies/{role_id}`,
    tag: "crews",
    summary: "Set a role's skill policy (allowed packs and action kinds, crew budget)",
    access: "manager",
    params: RoleSkillPolicyParams,
    body: RoleSkillPolicyPut,
    idempotency: "optional",
    response: { status: 200, schema: RoleSkillPolicy },
    errors: [],
    emits: ["role_skill_policy.changed"],
  }),
  "roleSkillPolicies.delete": defineRoute({
    ...agents,
    id: "roleSkillPolicies.delete",
    method: "delete",
    path: `${base}/role-skill-policies/{role_id}`,
    tag: "crews",
    summary: "Remove a role's skill policy (its crews lose `alone` and skill packs)",
    access: "manager",
    params: RoleSkillPolicyParams,
    response: { status: 204 },
    errors: [],
    emits: ["role_skill_policy.changed"],
  }),
  "crewCaps.get": defineRoute({
    ...agents,
    id: "crewCaps.get",
    method: "get",
    path: `${base}/crew-caps`,
    tag: "crews",
    summary: "The company's crew caps (per crew and for all crews)",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: CrewCap },
    errors: [],
    emits: [],
  }),
  "crewCaps.put": defineRoute({
    ...agents,
    id: "crewCaps.put",
    method: "put",
    path: `${base}/crew-caps`,
    tag: "crews",
    summary: "Set the company's crew caps",
    access: "manager",
    params: CompanyParams,
    body: CrewCapPut,
    idempotency: "optional",
    response: { status: 200, schema: CrewCap },
    errors: [],
    emits: ["crew_cap.changed"],
  }),
} as const;
